import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Immutable from 'immutable';

import * as searchUnitActions from 'actions/searchUnitActions';
import SearchUnitCalendarLayout from 'layouts/SearchUnitCalendarLayout/SearchUnitCalendarLayout';
import * as optimizelyUtil from 'utils/optimizely';
import dates from 'utils/dates';
import calendarUtils from 'utils/calendar';
import LinkInput from 'components/LinkInput/LinkInput';
import { CALENDAR_DATE_FORMAT } from 'constants/dates';
import { flexibilityItems, MONTH_FLEXIBILITY_VALUE } from 'constants/searchUnit';
import { dayInMilliseconds } from 'utils/utils';
import wrapWithCustomControl from 'layouts/CustomControlLayout/CustomControlWrapperLayout';
import CustomControlWrapper from 'layouts/CustomControlLayout/CustomControlWrapper';
import CustomControlPopoverWrapper from 'components/CustomControl/CustomControlPopoverWrapper';

import './SearchUnitDepartureDateLayout.scss';

const mapStateToProps = state => ({
  appState: state.appState,
  baseState: state.baseState,
  featureFlags: state.featureFlags,
  hotelOnly: state.hotelOnly,
});

const mapDispatchToProps = dispatch => ({
  searchUnitActionsBound: bindActionCreators(searchUnitActions, dispatch),
});

export class SearchUnitDepartureDateLayout extends Component {
  static propTypes = {
    appState: PropTypes.instanceOf(Immutable.Map),
    baseState: PropTypes.instanceOf(Immutable.Map),
    defaultDate: PropTypes.number,
    disableFlexibility: PropTypes.bool,
    disableAnyDate: PropTypes.bool,
    featureFlags: PropTypes.instanceOf(Immutable.Map),
    hideCustomControl: PropTypes.func.isRequired,
    hotelOnly: PropTypes.instanceOf(Immutable.Map),
    isCustomControlVisible: PropTypes.bool.isRequired,
    isPanda: PropTypes.bool,
    isHotelOnly: PropTypes.bool,
    isWholeMonthCalendarEnabled: PropTypes.bool,
    isDatePickerWithPricesEnabled: PropTypes.bool,
    labelText: PropTypes.string,
    location: PropTypes.shape({
      search: PropTypes.string,
      hash: PropTypes.string,
      query: PropTypes.shape({
        state: PropTypes.string,
      }),
    }),
    searchUnitActionsBound: PropTypes.shape({
      clearDepartureDate: PropTypes.func.isRequired,
      setDepartureDateFlexibility: PropTypes.func.isRequired,
      loadDayOfMonthData: PropTypes.func.isRequired,
    }).isRequired,
    showCustomControl: PropTypes.func.isRequired,
  };

  static defaultProps = {
    defaultDate: 0,
    disableAnyDate: false,
    disableFlexibility: false,
    featureFlags: Immutable.Map(),
    isDatePickerWithPricesEnabled: false,
    isHotelOnly: false,
    isPanda: false,
    isWholeMonthCalendarEnabled: false,
    labelText: 'Depart:',
    location: {},
  };

  componentDidMount() {
    const { baseState, disableFlexibility, isPanda } = this.props;

    if (!isPanda) {
      const isDateSelected = baseState.getIn(['queryState', 'props', 'deptDate', 'selected']);
      const isFlexibilitySet = baseState.getIn(['queryState', 'props', 'deptDateFlexibility', 'selected']);

      if (isDateSelected && !isFlexibilitySet && !disableFlexibility) {
        this.setDefaultFlexibility();
      }
    }
  }

  setDefaultFlexibility() {
    const { featureFlags, searchUnitActionsBound } = this.props;
    const isFlexi3DaysEnabled = featureFlags.get('SearchUnitFlexi3DaysEnabled');

    if (isFlexi3DaysEnabled) {
      searchUnitActionsBound.setDepartureDateFlexibility(3);
    }
  }

  showCalendarControl = () => {
    this.props.showCustomControl();
  }

  get calendarControl() {
    const {
      appState,
      baseState,
      hideCustomControl,
      isHotelOnly,
      isWholeMonthCalendarEnabled,
      isDatePickerWithPricesEnabled,
      searchUnitActionsBound,
    } = this.props;

    const width = appState.get('isMobileBreakpoint') ? 270 : 380;
    const inputRefBounds = this.inputRef.getBoundingClientRect();
    const coordinatesOfAssociatedElement = {
      top: this.inputRef.offsetTop,
      left: this.inputRef.offsetLeft,
      width: inputRefBounds.width,
      height: inputRefBounds.height,
    };

    const loadDayOfMonthData = isHotelOnly ? undefined : searchUnitActionsBound.loadDayOfMonthData;

    return (
      <CustomControlWrapper closeCustomControl={hideCustomControl}>
        <CustomControlPopoverWrapper coordinatesOfAssociatedElement={coordinatesOfAssociatedElement} width={width} alignRight>
          <SearchUnitCalendarLayout
            baseState={baseState}
            isHotelOnly={isHotelOnly}
            isWholeMonthCalendarEnabled={!isHotelOnly && isWholeMonthCalendarEnabled}
            loadDayOfMonthData={loadDayOfMonthData}
            selectedDate={this.getDeptDate()}
            showPricing={isDatePickerWithPricesEnabled}
          />
        </CustomControlPopoverWrapper>
      </CustomControlWrapper>
    );
  }

  onClearDate = () => {
    const { searchUnitActionsBound, baseState, isWholeMonthCalendarEnabled } = this.props;

    const selectedFlexibility = baseState.getIn(['queryState', 'props', 'deptDateFlexibility', 'value']);
    if (isWholeMonthCalendarEnabled && selectedFlexibility === MONTH_FLEXIBILITY_VALUE) {
      searchUnitActionsBound.clearDepartureDateAndFlexibility();
    } else {
      searchUnitActionsBound.clearDepartureDate();
    }
  };

  onFlexibilityChange = (event) => {
    const { searchUnitActionsBound } = this.props;
    const value = event.currentTarget.value;

    searchUnitActionsBound.setDepartureDateFlexibility(value);
    this.reportFlexibilityChanged();
  };

  reportFlexibilityChanged() {
    const { featureFlags } = this.props;
    const message = featureFlags.get('SearchUnitFlexi3DaysEnabled') ? '3Days' : '0Days';
    optimizelyUtil.fireEvent(`SearchUnitFlexibilitySelect${message}`, 'clicked');
  }

  getDeptDate() {
    const { baseState, isPanda } = this.props;
    return parseInt(calendarUtils.getDepartureDate(baseState, isPanda), 10);
  }

  getVisibleFlexibilityItems() {
    const { isWholeMonthCalendarEnabled } = this.props;
    let visibleFlexibilityItems = flexibilityItems;
    if (isWholeMonthCalendarEnabled) {
      visibleFlexibilityItems = visibleFlexibilityItems.filter(({ value }) => value !== MONTH_FLEXIBILITY_VALUE);
    }

    return visibleFlexibilityItems;
  }

  saveInputRef = (ref) => {
    this.inputRef = ref;
  };

  render() {
    const {
      baseState,
      disableAnyDate,
      disableFlexibility,
      isCustomControlVisible,
      isWholeMonthCalendarEnabled,
      labelText,
      location,
    } = this.props;

    const deptDate = this.getDeptDate();
    const selectedFlexibility = baseState.getIn(['queryState', 'props', 'deptDateFlexibility', 'value']);
    const format = (selectedFlexibility === MONTH_FLEXIBILITY_VALUE && isWholeMonthCalendarEnabled) ? 'MMM YY' : CALENDAR_DATE_FORMAT;
    const datePickerValue = deptDate ? dates.format(deptDate * dayInMilliseconds, format) : '';

    return (
      <div className="search-unit-departure-date-layout">
        { datePickerValue ? (
          <div>
            <div ref={this.saveInputRef}>
              <LinkInput
                hasCalendarIcon
                hasTag={!disableAnyDate}
                label={labelText}
                location={location}
                onClick={this.showCalendarControl}
                onTagClick={this.onClearDate}
              >
                {datePickerValue}
              </LinkInput>
            </div>
            { isCustomControlVisible && this.calendarControl }{ !disableFlexibility && (selectedFlexibility !== MONTH_FLEXIBILITY_VALUE || !isWholeMonthCalendarEnabled) && (
              <div>
                <div className="search-unit-departure-date-layout__flexibility-label">Flexibility:</div>
                <select className="search-unit-departure-date-layout__flexibility" onChange={this.onFlexibilityChange} value={selectedFlexibility}>
                  { this.getVisibleFlexibilityItems().map(({ label, value }) =>
                    <option key={value} value={value}>{label}</option>,
                  )}
                </select>
              </div>
            ) }
          </div>
        ) : (
          <div ref={this.saveInputRef}>
            <LinkInput
              hasCalendarIcon
              label={labelText}
              location={location}
              onClick={this.showCalendarControl}
              onTagClick={this.onClearDate}
              placeholder={`${disableAnyDate ? 'Select' : 'Any'} Date`}
            />
            { isCustomControlVisible && this.calendarControl }
          </div>
        )}
      </div>
    );
  }
}

export default wrapWithCustomControl(connect(mapStateToProps, mapDispatchToProps)(SearchUnitDepartureDateLayout));



/** WEBPACK FOOTER **
 ** ./src/layouts/SearchUnitDepartureDateLayout/SearchUnitDepartureDateLayout.jsx
 **/